import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, ShieldCheck, Clock, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { whatsappLinks } from "@/lib/whatsapp";

/**
 * Wiederverwendbare Reparatur-Landing für nicht-Smartphone-Kategorien.
 * Zeigt Modell-Gruppen (Chips) → gefilterte Modell-Karten → Klick öffnet
 * Detail-Panel mit den vom Admin gepflegten Reparaturarten & Preisen.
 * Preis kann `null` sein → "Preis auf Anfrage" → WhatsApp/Telefon-CTA.
 */
export default function RepairCategoryPage({
  category,        // "ipad" | "macbook"
  eyebrow,         // Kleiner Titel oben
  headline,        // Groß
  subline,         // Untertitel
  accentColor = "#DC1928",
}) {
  const [groups, setGroups] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [services, setServices] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingServices, setLoadingServices] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingModels(true);
      try {
        const [g, m] = await Promise.all([
          api.get(`/repair-catalog/${category}/groups`),
          api.get(`/repair-catalog/${category}/models`),
        ]);
        if (!alive) return;
        setGroups(g.data.groups || []);
        setModels(m.data.models || []);
      } catch (e) {
        console.error(e);
      } finally {
        alive && setLoadingModels(false);
      }
    })();
    return () => { alive = false; };
  }, [category]);

  const filteredModels = useMemo(() => {
    if (!selectedGroup) return models;
    return models.filter((m) => m.group === selectedGroup);
  }, [models, selectedGroup]);

  const openModel = async (model) => {
    setSelectedModel(model);
    setServices([]);
    setLoadingServices(true);
    try {
      const { data } = await api.get(`/repair-catalog/model/${model.id}/services`);
      setServices(data.services || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingServices(false);
    }
  };

  return (
    <div className="pt-32 pb-24 bg-[#0A0A0B] min-h-screen relative" data-testid={`repair-${category}-page`}>
      {/* Ambient background */}
      <div className="absolute inset-x-0 top-0 h-[500px] pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-15 blur-[160px]" style={{ background: accentColor }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        {/* HERO */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur">
            <Wrench className="w-3.5 h-3.5" style={{ color: accentColor }} />
            <span className="text-[10px] uppercase tracking-[0.24em] text-white font-bold">{eyebrow}</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mt-5 leading-[1.0]">
            {headline}
          </h1>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-2xl">
            {subline}
          </p>
        </motion.div>

        {/* Trust badges */}
        <div className="mt-10 grid grid-cols-3 gap-3 md:gap-4 max-w-2xl">
          <TrustBadge icon={ShieldCheck} label="6 Monate Garantie" />
          <TrustBadge icon={Clock} label="Meist innerhalb 24–72h" />
          <TrustBadge icon={Wrench} label="Original & OEM Ersatzteile" />
        </div>

        {/* GROUP FILTER */}
        {groups.length > 0 && (
          <div className="mt-14">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-bold mb-3">Modellgruppe</div>
            <div className="flex gap-2 flex-wrap" data-testid="group-filter">
              <button
                onClick={() => setSelectedGroup(null)}
                className={`px-4 h-10 rounded-full text-xs font-semibold transition-all border ${
                  selectedGroup === null
                    ? "bg-white text-[#0A0A0B] border-white"
                    : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"
                }`}
                data-testid="group-filter-all"
              >
                Alle Modelle
              </button>
              {groups.map((g) => (
                <button
                  key={g}
                  onClick={() => setSelectedGroup(g)}
                  className={`px-4 h-10 rounded-full text-xs font-semibold transition-all border ${
                    selectedGroup === g
                      ? "bg-white text-[#0A0A0B] border-white"
                      : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"
                  }`}
                  data-testid={`group-filter-${g.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MODEL GRID */}
        <div className="mt-8">
          {loadingModels ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-36 rounded-3xl bg-white/[0.03] border border-white/5 animate-pulse"></div>
              ))}
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="p-12 rounded-3xl border border-dashed border-white/10 bg-[#0F0F11] text-center" data-testid="models-empty">
              <p className="text-sm text-zinc-500">Für diese Gruppe sind aktuell keine Modelle hinterlegt.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="models-grid">
              {filteredModels.map((m) => (
                <ModelCard key={m.id} model={m} onClick={() => openModel(m)} accentColor={accentColor} />
              ))}
            </div>
          )}
        </div>

        {/* SERVICES PANEL */}
        {selectedModel && (
          <ServicesPanel
            model={selectedModel}
            services={services}
            loading={loadingServices}
            onClose={() => setSelectedModel(null)}
            category={category}
            accentColor={accentColor}
          />
        )}

        {/* Fallback CTA */}
        <div className="mt-20 p-6 md:p-8 rounded-3xl border border-white/8 bg-gradient-to-br from-[#13130F] via-[#0E0E0B] to-[#0A0A0B]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-bold">Modell nicht dabei?</div>
              <p className="text-sm text-zinc-400 mt-2 max-w-xl">
                Wir reparieren auch ältere und exotischere {category === "ipad" ? "iPads" : "MacBooks"}. Melde dich einfach bei uns — meist finden wir eine Lösung.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={whatsappLinks.generic().href}
                onClick={whatsappLinks.generic().onClick}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0B] font-semibold btn-lift"
                data-testid="fallback-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 font-semibold"
              >
                Kontaktformular <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrustBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/8">
      <Icon className="w-4 h-4 text-emerald-400 shrink-0" />
      <span className="text-xs md:text-sm text-zinc-300 font-medium">{label}</span>
    </div>
  );
}

function ModelCard({ model, onClick, accentColor }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left rounded-3xl overflow-hidden border border-white/8 bg-gradient-to-br from-[#15151A] via-[#101013] to-[#0A0A0B] hover:border-white/20 transition-colors p-5"
      data-testid={`model-card-${model.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">{model.group}</div>
          <div className="font-display text-lg font-bold text-white mt-1 leading-tight">{model.name}</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{model.year || ""}</span>
      </div>
      <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
        <span className="text-zinc-500">Reparatur ansehen</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" style={{ color: accentColor }} />
      </div>
    </button>
  );
}

function ServicesPanel({ model, services, loading, onClose, category, accentColor }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 md:p-10 overflow-y-auto"
      onClick={onClose}
      data-testid="services-panel"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#0F0F11] border border-white/10 rounded-3xl p-6 md:p-8 text-white"
      >
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">{model.group}</div>
            <h3 className="font-display text-2xl font-bold text-white mt-1">{model.name}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-2xl leading-none" aria-label="Schließen" data-testid="services-close">✕</button>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : services.length === 0 ? (
          <div className="py-14 text-center">
            <p className="text-sm text-zinc-500 mb-1">Preise werden gerade gepflegt.</p>
            <p className="text-xs text-zinc-600 mb-6">Wir erstellen dir gerne ein individuelles Angebot per WhatsApp oder Telefon.</p>
            <div className="flex gap-2 justify-center">
              <a href={whatsappLinks.generic().href} onClick={whatsappLinks.generic().onClick} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0A0B] font-semibold text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp anfragen
              </a>
              <a href="tel:+436764440304" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 text-sm">Anrufen</a>
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="services-list">
            {services.map((s) => (
              <ServiceRow key={s.id} service={s} model={model} accentColor={accentColor} />
            ))}
          </div>
        )}

        <p className="mt-6 text-[11px] text-zinc-600 leading-relaxed">
          Alle Preise brutto inkl. 20 % USt. Preise können je nach Zustand des Geräts abweichen — der finale Preis wird nach Sichtprüfung vor Ort bestätigt.
        </p>
      </div>
    </div>
  );
}

function ServiceRow({ service, model, accentColor }) {
  const onRequest = service.on_request || service.price == null;
  const wa = whatsappLinks.repair
    ? whatsappLinks.repair({ model: model.name, issue: service.repair_type })
    : whatsappLinks.generic({ text: `Anfrage: ${model.name} — ${service.repair_type}` });

  return (
    <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.03] border border-white/8" data-testid={`service-${service.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-white text-sm">{service.repair_type}</div>
        {(service.duration || service.description) && (
          <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
            {service.duration && <span className="mr-2">{service.duration}</span>}
            {service.description}
          </div>
        )}
      </div>
      {onRequest ? (
        <a
          href={wa.href}
          onClick={wa.onClick}
          target="_blank" rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1 px-4 h-10 rounded-full bg-white/5 border border-white/15 text-xs font-semibold text-white hover:bg-white/10"
          data-testid={`service-on-request-${service.id}`}
        >
          Preis anfragen <ArrowRight className="w-3.5 h-3.5" />
        </a>
      ) : (
        <div className="shrink-0 text-right">
          <div className="font-display text-xl font-bold text-white numeric">€ {Number(service.price).toFixed(0)}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">inkl. USt</div>
        </div>
      )}
    </div>
  );
}
